import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "../ui/button";

/** Go-live readiness: there is no mock engine any more — every snipe fires a
 *  real TransIP registration. This panel shows exactly what still blocks a
 *  real registration (token expiry, incomplete contact profile) and offers a
 *  read-only deep credential probe. */
export const LiveModePanel = () => {
  const [status, setStatus] = useState(null);   // cheap /status readiness
  const [probe, setProbe] = useState(null);      // deep /registrars/verify result
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/status");
      setStatus(data);
    } catch { /* best-effort */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/registrars/verify");
      setProbe(data);
      toast[data.can_go_live ? "success" : "error"](
        data.can_go_live ? "Credentials verified — ready to register" : "Not ready yet — see the checklist"
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  const ready = status?.ready_to_register;
  const token = status?.transip_token || {};
  const missing = status?.contact_missing || [];
  const hasKey = probe?.transip_auth?.startsWith("private key");

  const tokenLabel = (() => {
    if (hasKey) return "TransIP auth — private key (never expires)";
    if (!token.present) return "TransIP token — not set";
    if (token.expired) return `TransIP token — EXPIRED${token.expires_at ? ` on ${token.expires_at.slice(0, 10)}` : ""}`;
    if (token.unreadable) return "TransIP token — present (unreadable expiry)";
    return `TransIP token — valid${token.expires_at ? ` until ${token.expires_at.slice(0, 10)}` : ""}`;
  })();
  const tokenOk = hasKey || (token.present && !token.expired);

  return (
    <div className="border border-[#eaecf0] rounded-sm" data-testid="live-mode-panel">
      <div className="px-4 py-3 border-b border-[#eaecf0] bg-[#FAFAFA] flex items-center justify-between gap-3">
        <div>
          <div className="overline">Go-live readiness</div>
          <div className="text-sm font-medium flex items-center gap-2">
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm"
              style={ready ? { background: "#ECFDF5", color: "#047857" } : { background: "#FEF3C7", color: "#B45309" }}
              data-testid="live-mode-badge"
            >
              {ready ? "READY" : "SETUP NEEDED"}
            </span>
            {ready ? "Real registrations ready" : "Real engine — setup incomplete"}
          </div>
        </div>
        <Button
          type="button" variant="outline" className="rounded-sm text-xs shrink-0"
          onClick={verify} disabled={verifying} data-testid="verify-credentials-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
          {verifying ? "Verifying…" : "Verify credentials"}
        </Button>
      </div>

      <div className="p-4 space-y-3 text-xs font-mono">
        <div className="flex items-start gap-2 text-[#525252]" data-testid="live-engine-note">
          <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#047857]" strokeWidth={1.6} />
          <span>There is no simulation mode — every snipe and instant register fires a <b>real</b> TransIP call and spends real money once the checklist below is green.</span>
        </div>

        {/* Readiness checklist (cheap /status — no API cost) */}
        <Line ok={status?.transip_configured} label={`TransIP credentials — ${status?.transip_configured ? "configured" : "not set"}`} />
        <Line ok={tokenOk} label={tokenLabel} />
        {!hasKey && !tokenOk && (
          <div className="pl-6 text-[11px] text-[#B45309]">
            Paste a fresh Bearer JWT (or a login + PEM private key for auto-refresh) in{" "}
            <a href="/settings?cat=registrars" className="underline">TransIP settings</a>.
          </div>
        )}
        <Line ok={status?.contact_configured}
          label={`Contact profile — ${status?.contact_configured ? "complete" : "incomplete (required by SIDN for .nl)"}`} />
        {missing.length > 0 && (
          <div className="pl-6 text-[11px] text-[#B45309]" data-testid="contact-missing">
            Still needed:{" "}
            {missing.map((k) => k.replace("CONTACT_", "").replace(/_/g, " ").toLowerCase()).join(", ")}
            {" — "}
            <a href="/settings?cat=contact" className="underline">fill them in</a>
          </div>
        )}

        {/* Deep probe results (only after clicking Verify) */}
        {probe?.registrars && (
          <div className="pt-1 border-t border-[#f0f0f0] space-y-1">
            <div className="text-[11px] text-[#A1A1AA] pt-1">Live credential probe (read-only — nothing registered):</div>
            {probe.registrars.map((r) => (
              <div key={r.registrar} className="space-y-1" data-testid={`verify-${r.registrar}`}>
                <Line
                  ok={r.ok}
                  label={`${r.registrar.toUpperCase()} — ${r.configured ? r.summary : "credentials not set"}${
                    r.latency_ms ? ` · ${r.latency_ms}ms` : ""
                  }`}
                />
                {(r.checks || []).map((c, i) => (
                  <div key={i} className="pl-6 text-[11px] text-[#525252]">
                    {c.ok ? "✓" : "✕"} {c.name}: {c.message}
                  </div>
                ))}
              </div>
            ))}
            <p className="text-[#A1A1AA] pt-1">
              Checked {probe.checked_at?.slice(0, 19).replace("T", " ")}
            </p>
          </div>
        )}

        {!probe && (
          <p className="text-[#A1A1AA]" data-testid="verify-hint">
            Tip: run “Verify credentials” for a live read-only probe of each registrar's API keys.
            It only authenticates and runs one availability lookup — it never registers anything.
          </p>
        )}
      </div>
    </div>
  );
};

const Line = ({ ok, label }) => (
  <div className="flex items-start gap-2">
    {ok
      ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-[#047857] shrink-0" strokeWidth={1.5} />
      : <XCircle className="w-3.5 h-3.5 mt-0.5 text-[#BE123C] shrink-0" strokeWidth={1.5} />}
    <span className="text-[#525252] break-all">{label}</span>
  </div>
);

export default LiveModePanel;
